import { kc } from './keycloak';
import { isConnectivityError } from '../monitoring/connectivity';

/**
 * The session-expiry channel.
 *
 * keycloak-js rejects `updateToken` once the SSO session itself has ended, and
 * that rejection is the only signal the application gets. It surfaces in the
 * axios interceptors, the socket connect and 24 component call sites — none of
 * which sit in the React tree, so this is a module-level channel and not a
 * context.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let expired = false;

/**
 * Announce that the session is over. Fires each listener at most once: a
 * dashboard opens several requests at a time and every one of their refreshes
 * fails, which without this guard is one dialog per request.
 */
export const notifySessionExpired = (): void => {
  if (expired) return;
  expired = true;
  listeners.forEach((listener) => listener());
};

/**
 * A listener subscribing after the fact still hears it — the dialog mounts
 * inside a lazy route tree and can lose the race against the first failing
 * request.
 */
export const onSessionExpired = (listener: Listener): (() => void) => {
  listeners.add(listener);
  if (expired) listener();
  return () => {
    listeners.delete(listener);
  };
};

/**
 * The dialog was dismissed. Clears the latch but keeps the listeners, so the
 * next failing request raises it again — dismissing hides the message, it does
 * not mean the session came back. Without this the latch would stay set and the
 * dialog could never return, which is the silent failure this all exists to fix.
 */
export const acknowledgeSessionExpiry = (): void => {
  expired = false;
};

/** Test seam: forgets the listeners too, which nothing in the application does. */
export const resetSessionExpiry = (): void => {
  expired = false;
  listeners.clear();
};

/**
 * The status keycloak-js's own NetworkError carries when its token endpoint
 * answered. Recognised by shape, because keycloak-js does not export the class
 * in its types.
 */
const keycloakStatus = (error: unknown): number | undefined => {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : undefined;
};

/**
 * A refresh that never reached Keycloak says nothing about the session, so an
 * unreachable platform must not be reported as an expiry — telling a member the
 * wrong reason is what teaches them to ignore the message.
 */
const meansTheSessionEnded = (error: unknown): boolean => {
  const status = keycloakStatus(error);
  // A 5xx from Keycloak is the same thing to a member as no answer at all.
  if (status !== undefined) return status < 500;
  return !isConnectivityError(error);
};

/**
 * Refresh the access token, announcing an expired session before the caller's
 * own error handling runs. It rethrows on purpose: every caller has to keep
 * failing exactly as it did, because the dialog is additive and not a recovery.
 */
export const ensureFreshToken = async (minValidity = 30): Promise<void> => {
  try {
    await kc.updateToken(minValidity);
  } catch (error) {
    if (meansTheSessionEnded(error)) notifySessionExpired();
    throw error;
  }
};

/**
 * Decide whether a 401 means the session died. It usually does not —
 * a token with no account here gets 401 "Invalid user" from every route — so
 * the session is tested rather than assumed. `-1` forces a refresh round-trip
 * (keycloak-js `updateToken`); a token that still refreshes means the 401 came
 * from the endpoint's own rules and nothing is announced.
 */
export const reportUnauthorized = async (): Promise<void> => {
  try {
    await kc.updateToken(-1);
  } catch (error) {
    if (meansTheSessionEnded(error)) notifySessionExpired();
  }
};
